import { useState, useCallback, useRef } from "react";
import { env } from "@/lib/env";

export interface PaginationState {
  currentPage: number;
  pageSize: number;
  totalCount: number;
}

export interface PaginationActions {
  setCurrentPage: (page: number) => void;
  setTotalCount: (total: number) => void;
  resetPagination: () => void;
}

export interface PaginationUtils {
  calculateRange: (page?: number, overridePageSize?: number) => string;
  parseContentRange: (
    contentRange: string | null
  ) => { start: number; end: number; total: number } | null;
  getTotalPages: () => number;
  getDisplayRange: () => { start: number; end: number };
}

export interface UsePaginationReturn
  extends PaginationState,
  PaginationActions,
  PaginationUtils { }

export interface UsePaginationOptions {
  initialPage?: number;
  pageSize?: number;
}

export const usePagination = (
  options: UsePaginationOptions = {}
): UsePaginationReturn => {
  const { initialPage = 1, pageSize = env.DEFAULT_PAGE_SIZE } = options;

  // Keep pageSize in a ref so callbacks always use the latest value
  const pageSizeRef = useRef(pageSize);
  pageSizeRef.current = pageSize;

  const [currentPage, setCurrentPage] = useState(initialPage);
  const [totalCount, setTotalCount] = useState(0);

  const calculateRange = useCallback(
    (page?: number, overridePageSize?: number) => {
      const ps = overridePageSize ?? pageSizeRef.current;
      const targetPage = page ?? currentPage;
      const start = (targetPage - 1) * ps;
      const end = start + ps - 1;
      return `${start}-${end}`;
    },
    [currentPage]
  );

  const parseContentRange = useCallback((contentRange: string | null) => {
    if (!contentRange) return null;
    const match = contentRange.match(/(\d+)-(\d+)\/(\d+)/);
    if (!match) return null;
    return {
      start: Number.parseInt(match[1], 10),
      end: Number.parseInt(match[2], 10),
      total: Number.parseInt(match[3], 10),
    };
  }, []);

  const getTotalPages = useCallback(() => {
    return Math.ceil(totalCount / pageSizeRef.current);
  }, [totalCount]);

  const getDisplayRange = useCallback(() => {
    const ps = pageSizeRef.current;
    const start = (currentPage - 1) * ps + 1;
    const end = Math.min(currentPage * ps, totalCount);
    return { start, end };
  }, [currentPage, totalCount]);

  const resetPagination = useCallback(() => {
    setCurrentPage(initialPage);
    setTotalCount(0);
  }, [initialPage]);

  return {
    currentPage,
    pageSize: pageSizeRef.current,
    totalCount,
    setCurrentPage,
    setTotalCount,
    resetPagination,
    calculateRange,
    parseContentRange,
    getTotalPages,
    getDisplayRange,
  };
};
